"use client";

import { DayPicker } from "react-day-picker";
import { ko } from "date-fns/locale";
import { useCalendarDate } from "./_hooks/use-calendar-date";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../../../lib/utils";

import { format } from "date-fns";
import { defaultTimeSlots, months } from "./constant/calendar";
import TitleAndThreeButtonCaption from "./captions/title-and-three-button-caption";
import { TimeSlotWithDate } from "./interface/date.interface";
import { v4 as uuidv4 } from "uuid";
interface TimeSlot {
  startTime: string; // HH:mm 형식
  endTime: string; // HH:mm 형식
  date?: string; // YYYY-MM-DD 형식
}

interface CalendarWithOptionsProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  className?: string;
  selectedTimeSlot: TimeSlotWithDate | null;
  setSelectedTimeSlot: (timeSlot: TimeSlotWithDate) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export function CalendarWithOptionsSmall({
  date,
  onDateChange,
  className,
  selectedTimeSlot,
  setSelectedTimeSlot,
  onConfirm,
  onCancel,
}: CalendarWithOptionsProps) {
  const {
    tempSelectedDate,

    currentDate,
    handleYearChange,
    handleMonthChange,
    handleDaySelect,
    handlePrevMonth,
    handleNextMonth,
    handleToday,
  } = useCalendarDate({
    initialDate: date,
    onDateChange,
  });

  const handleTimeSlotClick = (timeSlot: TimeSlot) => {
    if (!tempSelectedDate) {
      return;
    }

    const selectedDate = format(tempSelectedDate, "yyyy-MM-dd", { locale: ko });

    setSelectedTimeSlot({
      ...timeSlot,
      date: selectedDate,
      id: uuidv4(),
    });
  };

  const handleDaySelectWithMonthChange = (day: Date | undefined) => {
    if (!day) return;

    const newDate = new Date(day);
    newDate.setDate(1);

    if (
      day.getMonth() !== currentDate.getMonth() ||
      day.getFullYear() !== currentDate.getFullYear()
    ) {
      handleMonthChange(months[day.getMonth()], months);
      handleYearChange(day.getFullYear().toString());
    }

    handleDaySelect(day);
  };

  return (
    <div className={cn("flex h-full gap-sp400", className)}>
      <div className="flex-1 flex flex-col gap-sp80">
        <div className="border-b border-semantic-secondary-stroke-normal-default">
          <TitleAndThreeButtonCaption
            handlePrevMonth={handlePrevMonth}
            handleNextMonth={handleNextMonth}
            handleYearChange={handleYearChange}
            handleMonthChange={handleMonthChange}
            currentDate={currentDate}
            handleToday={handleToday}
          />
        </div>
        <div className="flex gap-sp80 h-[300px] ">
          <DayPicker
            mode="single"
            selected={tempSelectedDate}
            onSelect={handleDaySelectWithMonthChange}
            month={currentDate}
            locale={ko}
            showOutsideDays={true}
            weekStartsOn={1}
            classNames={{
              root: "w-[325px]",
              months:
                "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
              caption: "hidden",
              head_row: "flex justify-between",
              head_cell:
                "rounded-md w-full h-7 typography-semantic-label-1-normal---regular text-semantic-secondary-foreground-normal-default flex items-center justify-center [&:last-child]:text-semantic-error-global-global-default",
              row: "flex justify-between w-full h-[45.33px]",
              cell: "relative flex items-center justify-center text-center text-sm p-1 w-[46.43px] focus-within:relative focus-within:z-20",
              day: "size-9 p-0 font-normal aria-selected:opacity-100 rounded-full typography-semantic-label-1-normal---regular text-semantic-secondary-foreground-normal-default hover:bg-semantic-secondary-background-normal-hovered",
              day_selected:
                "bg-semantic-primary-background-normal-default hover:bg-semantic-primary-background-normal-default text-semantic-static-global-global-white",
              day_today:
                "border border-semantic-primary-background-normal-default",
              day_outside:
                "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30 aria-selected:bg-semantic-primary-background-normal-default aria-selected:text-semantic-static-global-global-white",
            }}
          />
          <div className="flex-1 flex flex-col h-full">
            <div className="h-full grid gap-sp80 px-sp80 py-sp120">
              {defaultTimeSlots.map((slot) => {
                const currentDate = tempSelectedDate
                  ? format(tempSelectedDate, "yyyy-MM-dd", { locale: ko })
                  : undefined;
                const isSelected =
                  selectedTimeSlot?.startTime === slot.startTime &&
                  selectedTimeSlot?.endTime === slot.endTime &&
                  selectedTimeSlot?.date === currentDate;

                return (
                  <Button
                    state="secondary"
                    size="xsm"
                    variant="outline"
                    onClick={() => handleTimeSlotClick(slot)}
                    className={cn(
                      "p-3 rounded w-full flex justify-between items-center",
                      "hover:bg-semantic-secondary-background-normal-hovered"
                    )}
                    // disabled={isSelected || !tempSelectedDate}
                    data-state={isSelected ? "active" : "default"}
                  >
                    <span className="w-full flex items-center justify-center gap-sp40 typography-semantic-label-2-medium text-semantic-secondary-foreground-heavy-default">
                      {slot.startTime} - {slot.endTime}
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-sp80 p-sp160 border-t border-semantic-secondary-stroke-normal-default">
          <Button variant="transparent" state="secondary" onClick={onCancel}>
            취소
          </Button>
          <Button variant="transparent" state="secondary" onClick={onConfirm}>
            확인
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CalendarWithOptionsSmall;
